import { useState, useEffect, useCallback } from 'react';
import StateMachineService from '@/domain/StateMachine/StateMachineService';

export interface StateMachineStrategy {
	value: string;
	label: string;
}

export interface StateMachineWithStrategies {
	id: number;
	name: string;
	strategies: StateMachineStrategy[];
	enabled: boolean;
	locationId: number;
	locationName: string;
	inpId: number;
	inpName: string;
}

interface UseStateMachinesParams {
	inpId: string | number | null;
	page?: number;
	size?: number;
	onlyEnabled?: boolean;
}

interface UseStateMachinesReturn {
	stateMachines: StateMachineWithStrategies[];
	loading: boolean;
	error: string | null;
	refetch: () => void;
}

const stateMachineService = new StateMachineService();

export default function useStateMachines({
	inpId,
	page = 1,
	size = 100,
	onlyEnabled = false,
}: UseStateMachinesParams): UseStateMachinesReturn {
	const [stateMachines, setStateMachines] = useState<StateMachineWithStrategies[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchStateMachines = useCallback(async () => {
		if (!inpId) {
			setStateMachines([]);
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const response = await stateMachineService.getAllStateMachines({
				inpId: String(inpId),
				page,
				size,
			});

			if (response?.data) {
				const processedData: StateMachineWithStrategies[] = response.data.map((sm: any) => {
					const strategiesArray = sm.strategies
						? sm.strategies.split(',').map((s: string) => s.trim()).filter(Boolean)
						: [];

					const strategies: StateMachineStrategy[] = strategiesArray.map((strategy: string) => ({
						value: strategy,
						label: strategy,
					}));

					return {
						id: sm.id,
						name: sm.name,
						strategies,
						enabled: sm.enabled,
						locationId: sm.locationId,
						locationName: sm.locationName,
						inpId: sm.inpId,
						inpName: sm.inpName,
					};
				});

				const filteredData = onlyEnabled ? processedData.filter((sm) => sm.enabled === true) : processedData;
				setStateMachines(filteredData);
			}
		} catch (err) {
			console.error('Error fetching state machines:', err);
			setError('Failed to fetch state machines');
			setStateMachines([]);
		} finally {
			setLoading(false);
		}
	}, [inpId, page, size, onlyEnabled]);

	useEffect(() => {
		fetchStateMachines();
	}, [fetchStateMachines]);

	return {
		stateMachines,
		loading,
		error,
		refetch: fetchStateMachines,
	};
}
